
import React, { ReactNode } from 'react';
import { AuthContext } from './AuthContext';
import { useAuthInitialization } from './hooks/useAuthInitialization';
import { useAuthMethods } from './hooks/useAuthMethods';
import { useSessionExpiry } from './hooks/useSessionExpiry';
import { authHeaders } from '@/lib/authHeaders';
import { toast } from '@/hooks/use-toast';
import {
  buildRegistrationEnrollmentRequest,
  completePendingCourseEnrollment,
} from '@/utils/pendingCourseEnrollment';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {

  const {
    currentUser,
    setCurrentUser,
    authInitialized,
    authLoading,
    setAuthLoading,
    sessionExpireTime,
    setSessionExpireTime
  } = useAuthInitialization();

  const [isLoggingOut, setIsLoggingOut] = React.useState(false);

  const {
    login,
    logout,
    refreshSession
  } = useAuthMethods(setCurrentUser, setAuthLoading, setSessionExpireTime, setIsLoggingOut);

  // Set up session expiration check and auto-refresh
  useSessionExpiry(sessionExpireTime, setCurrentUser);

  React.useEffect(() => {
    if (!currentUser?.email) return;
    let active = true;
    let enrolledCourseIds: string[] = [];

    void completePendingCourseEnrollment({
      storage: window.localStorage,
      signedInEmail: currentUser.email,
      enroll: async (pending) => {
        const request = buildRegistrationEnrollmentRequest(pending);
        const response = await fetch(request.url, {
          method: 'POST',
          headers: await authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(request.body),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(body.error || 'Could not complete course enrollment.');
        }
        enrolledCourseIds = Array.isArray(body.courseIds) && body.courseIds.length
          ? body.courseIds
          : [pending.courseId];
      },
    }).then((pending) => {
      if (active && pending) {
        window.location.assign(enrolledCourseIds.length > 1 ? '/courses' : pending.redirect);
      }
    }).catch((error) => {
      if (!active) return;
      console.error('Failed to complete magic-link course enrollment:', error);
      toast({
        title: 'Course enrollment needs attention',
        description: 'You are signed in, but we could not enroll you in the course. Please try again.',
        variant: 'destructive',
      });
    });

    return () => { active = false; };
  }, [currentUser?.id, currentUser?.email]);

  const value = {
    currentUser,
    login,
    logout,
    refreshSession,
    authLoading,
    isLoggingOut,
    sessionExpireTime
  };

  // Only render children once auth has been initialized
  if (!authInitialized) {
    console.log("Auth not yet initialized, showing loading");
    return (
      <div className="flex justify-center items-center h-screen">
        <p>Initializing authentication...</p>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
